import React, { useContext, useState, useEffect } from 'react';
import ExportCsv from 'components/export/csv';
import ExportProgress from 'components/export/progress';
import useExportData from './exportData';
import useVideoExportData from './videoExport';
import useAudioExportData from './audioExport';
import { Context } from './context';
import { FetchData } from './fetchData';

type Phase = 'pages' | 'downloads' | 'csv';

interface Progress {
  phase: Phase,
  completed: number,
  total: number,
  startedAtMs: number,
}

const initialProgress: Progress = { phase: 'pages', completed: 0, total: 0, startedAtMs: 0 };

// Each phase gets its own bar and its own clock, because they measure different
// work at different rates -- carrying one elapsed time across all three would make
// every estimate after the first phase wrong.
const PHASE_TITLES: Record<Phase, string> = {
  pages: 'Exporting',
  downloads: 'Fetching download history',
  csv: 'Building CSV file',
};

const PHASE_UNITS: Record<Phase, string> = {
  pages: 'pages',
  downloads: 'batches',
  csv: 'pages',
};

/**
 * Export status panel for the file, video and audio reports under
 * /marketing/reports. Mounted once from App.js, because the export button lives
 * in the report toolbar and the panel has to outlive whatever the user does with
 * the page after starting an export.
 */
const ReportProgressBar = (props) => {
  const { state, dispatch } = useContext(Context);
  const [progress, setProgress] = useState<Progress>(initialProgress);
  const [records, setRecords] = useState([]);
  const { getData } = useExportData({ onProgress: onDownloadProgress });
  const { getData: audioExportData } = useAudioExportData({ onProgress: onDownloadProgress });
  const { getData: videoExportData } = useVideoExportData({ onProgress: onDownloadProgress });
  const { exportClicked, exportVariables, queryVariables, query } = state;

  const csvLinkRef = React.useRef(null);
  const { phase, completed, total, startedAtMs } = progress;

  useEffect(() => {
    const isCsvRefExist = csvLinkRef && csvLinkRef.current;
    if (isCsvRefExist && records.length > 0) {
      csvLinkRef.current['link'].click();
      setRecords([]);
    }
  }, [records]);

  useEffect(() => {
    if (exportClicked) setProgress({ ...initialProgress, startedAtMs: Date.now() });
  }, [exportClicked]);

  /**
   * Advance the panel within a phase, or move it to a new one. Entering a phase
   * restarts its clock so the estimate reflects that phase's own throughput.
   */
  function reportProgress(phase: Phase, completed: number, total: number) {
    setProgress((prevState) => {
      if (prevState.phase !== phase) return { phase, completed, total, startedAtMs: Date.now() };
      if (prevState.completed === completed && prevState.total === total) return prevState;
      return { ...prevState, completed, total };
    });
  }

  function onPageProgress(done: number, count: number) { reportProgress('pages', done, count); }
  function onDownloadProgress(done: number, count: number) { reportProgress('downloads', done, count); }

  async function translatedData(data: unknown, isAllReceived: boolean, hasError: boolean = false) {
    if (hasError) {
      dispatch({ type: "resetReport", payload: false });
      return;
    }

    const itemType = String(queryVariables?.itemTypes?.[0]);
    let newData;
    if (itemType === "AUDIO") {
      newData = await audioExportData(data);
    } else if (itemType === "VIDEO") {
      newData = await videoExportData(data);
    } else {
      newData = await getData(data);
    }

    // Download history is fetched inside the transform above, so the CSV phase
    // only starts once that returns.
    reportProgress('csv', 1, 1);

    setRecords((prevState) => [
      ...prevState,
      ...[newData[0]],
      ...newData[1],
    ]);
    dispatch({ type: "resetReport", payload: false });
  }

  return (
    <>
      {exportClicked &&
        <>
          <ExportProgress
            title={PHASE_TITLES[phase]}
            completed={completed}
            total={total}
            unit={PHASE_UNITS[phase]}
            elapsedMs={startedAtMs > 0 ? Date.now() - startedAtMs : 0}
            showEstimate={phase !== 'csv'}
          />
          {query && queryVariables &&
            <FetchData
              totalRecords={exportVariables.totalRecords}
              returnData={translatedData}
              query={query}
              queryVariables={queryVariables}
              exportDataKey={'search'}
              setCurrentPage={onPageProgress}
            />}
          <ExportCsv data={records} ref={csvLinkRef} fileName={'report_export.csv'} />
        </>}
    </>
  );
};

export default ReportProgressBar;
